/**
 * rain_shelter — THE ROOF MAP (render wave 1, 2026-09-17).
 *
 * Rain and snow used to fall through every porch, awning, hangar mouth, cave
 * mouth and tree canopy, and the ground under them wetted and puddled like
 * the open street, because shelter was one flag (an interior is active) and
 * wetness one global uniform. After Lagarde's "Water drop 2a" (2012): render
 * a height map from ABOVE the player, then test each drop and each ground
 * fragment against it.
 *
 * THE FRAME. The world is 2,048 km under an FP64 origin rebase: children keep
 * ABSOLUTE coordinates and `scene.position` is −origin, so a `positionWorld`
 * read in a graph is RENDER space. The window's centre is kept in absolute
 * metres and the bounds uniform is refreshed every frame in render space
 * (`shelterBoundsRender`), so a rebase between two bakes cannot shift the
 * map under the world. The map stores the roof's ABSOLUTE height (render y
 * minus `scene.position.y` at bake) and the consumer compares against the
 * fragment's absolute height the same way — `shelterBounds.w` carries the
 * CURRENT `scene.position.y`.
 *
 * WHAT IS A ROOF. Anything on `RAIN_SHELTER_LAYER`: the static instancers'
 * batches (settlements, structures, world objects, decorations, admin
 * placements), the chunk-foliage batches that cast shadows (trees and rocks,
 * never groundcover — a grass blade must not dry the ground), the cave rock.
 * Terrain, water, sky, particles, characters and ships are never on it. The
 * map is vertical: it ignores the wind's tilt (a documented limitation; the
 * emitter already spawns upwind).
 *
 * The producer is `modules/RainShelterModule.ts`; the consumers are the ground
 * shader (`applyBiomeGround`: wetness, puddles, ripples and snow accumulation
 * scale by the exposure) and the GPU weather emitters (a sheltered drop
 * collapses to nothing through the material's size gate). `verify:rain-shelter`
 * pins the pure half; `verify:rain-shelter:probe` the bake on Dawn.
 */
import * as THREE from 'three/webgpu';
import { Fn, uniform, texture as tslTexture, float, vec2, smoothstep, select } from 'three/tsl';

/** Side length (m) of the window around the player. Past the near rain box and the groundcover ring. */
export const SHELTER_REGION_M = 96;
/** Re-centre once the player is this far from the middle. */
export const SHELTER_RECENTER_M = SHELTER_REGION_M * 0.25;
/** Map resolution (texels a side): 0.19 m a texel. */
export const SHELTER_RES = 512;
/** The bake camera sits this far above the player and sees this far down. */
export const SHELTER_CAM_ABOVE_M = 400;
export const SHELTER_CAM_FAR_M = 800;
/** Re-bake this often while it rains, snows or the ground is still drying. */
export const SHELTER_REBAKE_S = 2;
/** The scene layer a roof lives on (Atlas uses no other layer). */
export const RAIN_SHELTER_LAYER = 7;
/** A roof this little above a point starts to shelter it; this much above shelters it fully. */
export const SHELTER_OVER_MIN_M = 0.1;
export const SHELTER_OVER_MAX_M = 0.6;

// ── pure ──

export function shelterNeedsRecenter(centerX: number, centerZ: number, px: number, pz: number): boolean {
  return Math.abs(px - centerX) > SHELTER_RECENTER_M || Math.abs(pz - centerZ) > SHELTER_RECENTER_M;
}

/** The window's render-space bounds (minX, minZ, size) from its absolute centre and the current origin. */
export function shelterBoundsRender(centerAbsX: number, centerAbsZ: number, originX: number, originZ: number, size = SHELTER_REGION_M): [number, number, number] {
  return [centerAbsX - originX - size / 2, centerAbsZ - originZ - size / 2, size];
}

/** Map uv of a render-space point, or null outside the window. */
export function shelterUv(minX: number, minZ: number, size: number, x: number, z: number): [number, number] | null {
  const u = (x - minX) / size, v = (z - minZ) / size;
  if (u < 0 || u > 1 || v < 0 || v > 1) return null;
  return [u, v];
}

/**
 * Map uv → texture uv. The bake camera is rotated −90° about X, so its +Y is
 * world −Z: the texture's v runs toward −Z. Both the TSL sampler and the CPU
 * readback go through this ONE flip (three keeps a bottom-left uv origin for
 * render targets on both backends).
 */
export function shelterTexelUv(u: number, v: number): [number, number] { return [u, 1 - v]; }

const smooth01 = (t: number): number => { const s = t < 0 ? 0 : t > 1 ? 1 : t; return s * s * (3 - 2 * s); };

/**
 * Exposure of a point (1 in the open, 0 under a roof) from the map's sample:
 * `has` is the texel's alpha (1 where anything drew, filtered), `roofY` the
 * roof's absolute height it holds, `absY` the point's absolute height.
 */
export function shelterExposureCpu(has: number, roofY: number, absY: number): number {
  const over = roofY - absY;
  return 1 - has * smooth01((over - SHELTER_OVER_MIN_M) / (SHELTER_OVER_MAX_M - SHELTER_OVER_MIN_M));
}

// ── shared state (the producer writes, the consumers read) ──

/** (minX, minZ, size, sceneY) in RENDER space — refreshed every frame by the module. */
export const shelterBounds = new THREE.Vector4(-SHELTER_REGION_M / 2, -SHELTER_REGION_M / 2, SHELTER_REGION_M, 0);
export const shelterBoundsNode = uniform(shelterBounds);
/** 1 while the map is live (flag on, on a world, no interior active), else every point is exposed. */
export const shelterEnabled = uniform(0);

let target: THREE.RenderTarget | null = null;
let texNode: ReturnType<typeof tslTexture> | null = null;

/**
 * The roof map's render target — ONE object for the session, created on first
 * use, because TSL binds the texture OBJECT at graph build: the ground material
 * built before the module's first bake must already sample the real target.
 */
export function ensureShelterTarget(): THREE.RenderTarget {
  if (target) return target;
  target = new THREE.RenderTarget(SHELTER_RES, SHELTER_RES, {
    type: THREE.HalfFloatType, format: THREE.RGBAFormat,
    minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    depthBuffer: true, generateMipmaps: false,
  });
  target.texture.name = 'rain-shelter';
  return target;
}

/** The map as a TSL texture node (one node, reused by every consumer). */
export function shelterTextureNode(): ReturnType<typeof tslTexture> {
  if (!texNode) texNode = tslTexture(ensureShelterTarget().texture);
  return texNode;
}

/**
 * Exposure of a RENDER-space position: 1 in the open (and outside the window,
 * and while the map is off), 0 under a roof, a smoothstep between. The TSL of
 * `shelterExposureCpu`.
 */
export const shelterExposure = Fn(([wp]: [unknown]) => {
  const p = wp as unknown as { x: ReturnType<typeof float>; y: ReturnType<typeof float>; z: ReturnType<typeof float> };
  const b = shelterBoundsNode as unknown as { x: ReturnType<typeof float>; y: ReturnType<typeof float>; z: ReturnType<typeof float>; w: ReturnType<typeof float> };
  const u = p.x.sub(b.x).div(b.z), v = p.z.sub(b.y).div(b.z);
  const inside = u.greaterThanEqual(0).and(u.lessThanEqual(1)).and(v.greaterThanEqual(0)).and(v.lessThanEqual(1));
  const s = shelterTextureNode().sample(vec2(u, float(1).sub(v)) as never) as unknown as { r: ReturnType<typeof float>; a: ReturnType<typeof float> }; // shelterTexelUv
  const over = s.r.sub(p.y.sub(b.w));
  const sheltered = s.a.mul(smoothstep(float(SHELTER_OVER_MIN_M), float(SHELTER_OVER_MAX_M), over));
  const live = inside.and((shelterEnabled as unknown as ReturnType<typeof float>).greaterThan(0.5));
  return select(live, float(1).sub(sheltered), float(1));
});
